import React, { useEffect, useRef, useState } from "react";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { BellAlertIcon } from "@heroicons/react/24/outline"; // DİKKAT: BellAlertIcon kullanıyoruz
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";

// Token çekme
function getJwt() {
  const auth = localStorage.getItem("auth-storage");
  if (auth) {
    try {
      const parsed = JSON.parse(auth);
      return parsed.state?.accessToken || "";
    } catch {
      return "";
    }
  }
  return "";
}

// UserId çekme
function getUserId() {
  const user = localStorage.getItem("user");
  if (user) {
    try {
      const parsed = JSON.parse(user);
      return parsed.id || "";
    } catch {
      return "";
    }
  }
  return "";
}

const WS_URL = "https://dersmaster.com/ws";

const typeInfo = {
  CHAT: { emoji: "💬", color: "bg-blue-500" },
  LESSON_REQUEST: { emoji: "📚", color: "bg-green-500" },
  SYSTEM: { emoji: "⚙️", color: "bg-yellow-500" },
  PAYMENT: { emoji: "💳", color: "bg-purple-500" },
  DEFAULT: { emoji: "🔔", color: "bg-gray-400" }
};

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

const NotificationDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(getJwt());
  const [userId, setUserId] = useState(getUserId());
  const [stompClient, setStompClient] = useState(null);
  const navigate = useNavigate();
  const dropdownRef = useRef();

  // LocalStorage değişirse user ve token güncelle
  useEffect(() => {
    function sync() {
      setToken(getJwt());
      setUserId(getUserId());
    }
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  // İlk mount'ta bildirimleri fetch et
  useEffect(() => {
    if (token) fetchNotifications();
  }, [token]);

  // WebSocket ile anlık bildirim dinle
  useEffect(() => {
    if (!token || !userId) {
      // Token yoksa bağlantı açılmaz!
      return;
    }

    const subscribeAddress = `/topic/notifications/${userId}`;

    const client = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      connectHeaders: { Authorization: "Bearer " + token },
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(subscribeAddress, (message) => {
          try {
            const notif = JSON.parse(message.body);
            // SADECE CHAT HARİÇ DİĞERLERİNİ EKLE
            if (notif.type !== "CHAT") {
              setNotifications((prev) => {
                if (prev.some(n => n.id === notif.id)) return prev;
                return [notif, ...prev];
              });
            }
            // CHAT tipini eklemiyoruz, çünkü chat ayrı ikonun üstünde gösteriliyor
          } catch (e) {
            console.error("[NotificationDropdown] Bildirim parse hatası:", e);
          }
        });
      },
      onStompError: (frame) => {
        console.error("[NotificationDropdown] STOMP error", frame);
      }
    });

    client.activate();
    setStompClient(client);

    return () => {
      client.deactivate();
      setStompClient(null);
    };
  }, [token, userId]);

  // Dışarı tıklayınca dropdown kapansın
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // API ile bildirimleri çek (header’da token ile)
  async function fetchNotifications() {
    setLoading(true);
    try {
      const token = getJwt();
      if (!token) throw new Error("Token yok, fetch yapılamaz!");
      const res = await fetch("/v1/notifications", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const json = await res.json();
      // CHAT tipli bildirimleri filtrele
      setNotifications((json.data || []).filter(n => n.type !== "CHAT"));
    } catch (e) {
      console.error("[NotificationDropdown] Bildirim fetch error:", e);
      setNotifications([]);
    }
    setLoading(false);
  }

  // Dropdown açıldığında otomatik okundu yapma kodunu kaldıralım
  // useEffect(() => {
  //   if (open && notifications.length > 0) {
  //     markAllAsRead();
  //   }
  // }, [open]);

  // Manuel olarak tüm bildirimleri okundu işaretle
  async function markAllAsRead() {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;

    try {
      const token = getJwt();
      if (!token) return;

      // Yeni tek API çağrısı ile tüm bildirimleri okundu yap
      const response = await fetch(`/v1/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        // API başarılıysa local state'i güncelle
        setNotifications(notifications =>
          notifications.map(n => ({ ...n, read: true }))
        );
        console.log("Tüm bildirimler okundu olarak işaretlendi");
      } else {
        console.error("Bildirimler okundu işaretlenirken hata:", response.status);
      }
    } catch (error) {
      console.error("Bildirimler okundu işaretlenirken hata:", error);
    }
  }

  // Bildirimi okundu yap ve detayına git (artık gereksiz çünkü dropdown açılırken hepsi okundu oluyor)
  async function handleNotificationClick(notif) {
    setOpen(false);
    
    // Yönlendirme:
    if (notif.type === "LESSON_REQUEST") {
      navigate(`/lesson-requests/${notif.relatedId}`);
    } else if (notif.type === "PAYMENT") {
      navigate(`/payments/${notif.relatedId}`);
    } else if (notif.type === "SYSTEM") {
      navigate(`/system/${notif.relatedId}`);
    }
    // CHAT bildirimine yönlendirme yok!
  }

  // YALNIZCA CHAT HARİÇ unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Kullanıcı login değilse gösterme:
  if (!token) {
    return (
      <div className="relative">
        <button disabled className="relative p-2 rounded-full text-gray-400 cursor-not-allowed">
          <BellAlertIcon className="h-6 w-6" />
        </button>
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 z-[70] border border-gray-100 dark:border-gray-700 text-center text-gray-500 text-sm">
          <span className="text-xl">🔒</span>
          <div className="mt-2 font-semibold">Lütfen giriş yapın.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Bildirimler"
        className={clsx(
          "relative p-2 rounded-full transition-colors",
          open
            ? "bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-300"
            : "hover:bg-blue-50 dark:hover:bg-blue-900/30"
        )}
      >
        {/* DAHA MODERN BELL ALERT ICON */}
        <BellAlertIcon className="h-6 w-6 text-gray-500 dark:text-gray-200" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 text-[10px] font-extrabold bg-red-600 text-white rounded-full shadow border-2 border-white dark:border-gray-900 z-10 select-none animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[95vw] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 z-[70] animate-fade-in-down">
          <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="font-semibold text-gray-700 dark:text-gray-100 text-base">Bildirimler</span>
            <div className="flex items-center gap-2">
              {/* Hepsini Okundu İşaretle Butonu */}
              {unreadCount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsRead();
                  }}
                  className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                  title="Hepsini okundu işaretle"
                >
                  ✓ Hepsini Okundu
                </button>
              )}
              <Link
                to="/notifications"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                onClick={() => setOpen(false)}
              >
                Tümünü Gör
              </Link>
            </div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-gray-400">Yükleniyor...</div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Hiç bildirimin yok! 🥳</div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {notifications.slice(0, 7).map(notif => {
                const info = typeInfo[notif.type] || typeInfo.DEFAULT;
                return (
                  <li
                    key={notif.id}
                    className={clsx(
                      "flex gap-3 cursor-pointer px-4 py-3 transition-all group hover:bg-blue-50 dark:hover:bg-blue-900/30",
                      notif.read ? "opacity-70" : "bg-blue-50/40 dark:bg-blue-900/20"
                    )}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <span className={`flex-none h-10 w-10 rounded-xl flex items-center justify-center text-xl ${info.color}`}>
                      {info.emoji}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{notif.title}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{notif.content}</div>
                      <div className="text-[11px] mt-0.5 text-gray-400 dark:text-gray-500">{formatDate(notif.createdAt)}</div>
                    </div>
                    {!notif.read && (
                      <span className="ml-1 animate-ping rounded-full bg-red-500 w-2.5 h-2.5 mt-1" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <style>{`
        @keyframes fade-in-down {
          0% { opacity: 0; transform: translateY(-10px);}
          100% { opacity: 1; transform: translateY(0);}
        }
        .animate-fade-in-down {
          animation: fade-in-down 0.20s cubic-bezier(.61,.21,.39,.84) both;
        }
      `}</style>
    </div>
  );
};

export default NotificationDropdown;
